import { useTheme } from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import React, { type MouseEvent, type ReactNode, useMemo } from 'react';

import {
  CalendarIcon,
  CheckCircleIcon,
  DeleteIcon,
  EditIcon,
  RefreshIcon,
} from '../../../icons';
import { resourcesColors } from '../../../theme/tokens/resourcesColors';
import type { ResourceMenuItem } from '../../organisms/ResourceMenuActions';

export type ReminderRecurrenceType = 'none' | 'weekly' | 'monthly' | 'yearly' | string;

export type ReminderItem = {
  id: string;
  title: string;
  due_date?: string | Date | null;
  dueDate?: string | Date | null;
  is_completed?: boolean | null;
  isCompleted?: boolean | null;
  recurrence_type?: ReminderRecurrenceType | null;
  recurrenceType?: ReminderRecurrenceType | null;
  recurrence_value?: number | null;
  recurrenceValue?: number | null;
  description?: string | null;
  provider_id?: string | null;
  providerId?: string | null;
  provider_name?: string | null;
  providerName?: string | null;
  subject_id?: string | null;
  subjectId?: string | null;
  subject_name?: string | null;
  subjectName?: string | null;
  created_at?: string | Date | null;
};

export type ReminderCardAction = ResourceMenuItem;

export function formatReminderRecurrence(
  type?: ReminderRecurrenceType | null,
  value?: number | null,
): string | null {
  if (!type || type === 'none') return null;
  const val = value && value > 1 ? value : 1;
  switch (type) {
    case 'weekly':
      return val === 1 ? 'Chaque semaine' : `Toutes les ${val} semaines`;
    case 'monthly':
      return val === 1 ? 'Chaque mois' : `Tous les ${val} mois`;
    case 'yearly':
      return val === 1 ? 'Chaque année' : `Tous les ${val} ans`;
    default:
      return null;
  }
}

export type ReminderCardProps = {
  reminder?: ReminderItem;
  item?: ReminderItem;
  variant?: 'detailed' | 'condensed';
  displayProvider?: boolean;
  displaySubject?: boolean;
  providerHref?: string;
  onProviderClick?: () => void;
  renderProvider?: () => ReactNode;
  subjectHref?: string;
  onSubjectClick?: () => void;
  renderSubject?: () => ReactNode;
  renderDescription?: (description: string) => ReactNode;
  maxDescriptionLines?: number;
  isCompleting?: boolean;
  onComplete?: (reminder: ReminderItem) => void;
  onCreateEvent?: (reminder: ReminderItem) => void;
  onReopen?: (reminder: ReminderItem) => void;
  onEdit?: (reminder: ReminderItem) => void;
  onDelete?: (reminder: ReminderItem) => void;
  actions?: (ReminderCardAction | null | undefined | false)[];
  actionsButtonTooltip?: string;
  actionsButtonAriaLabel?: string;
  action?: ReactNode;
  dragHandle?: ReactNode;
  footerActions?: ReactNode;
  href?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  'data-testid'?: string;
};

type CondensedActionsInput = {
  isCompleted: boolean;
  isCompleting?: boolean;
  onComplete?: () => void;
  onCreateEvent?: () => void;
  onReopen?: () => void;
  managementActions: ReminderCardAction[];
};

/**
 * Condensed rows are scanned and tapped to navigate: every action lives in the
 * kebab menu so a stray tap can never complete a reminder.
 */
export function computeCondensedActions(input: CondensedActionsInput): ReminderCardAction[] {
  const workflowActions: ReminderCardAction[] = [];

  if (input.isCompleted) {
    if (input.onReopen) {
      workflowActions.push({
        id: 'reopen',
        label: 'Repasser en cours',
        icon: React.createElement(RefreshIcon, { fontSize: 'small' }),
        onClick: input.onReopen,
      });
    }
  } else {
    if (input.onComplete) {
      workflowActions.push({
        id: 'complete',
        label: 'Marquer comme terminé',
        icon: React.createElement(CheckCircleIcon, { fontSize: 'small' }),
        onClick: input.onComplete,
        disabled: input.isCompleting,
      });
    }
    if (input.onCreateEvent) {
      workflowActions.push({
        id: 'create-event',
        label: 'Créer un rendez-vous et terminer',
        icon: React.createElement(CalendarIcon, { fontSize: 'small' }),
        onClick: input.onCreateEvent,
      });
    }
  }

  if (workflowActions.length > 0 && input.managementActions.length > 0) {
    return [...workflowActions, { type: 'divider', id: 'workflow-divider' }, ...input.managementActions];
  }

  return [...workflowActions, ...input.managementActions];
}

export function useReminderCard(props: ReminderCardProps) {
  const theme = useTheme();
  const reminder = props.reminder ?? props.item ?? { id: '', title: '' };

  const isCompleted = Boolean(reminder.is_completed ?? reminder.isCompleted);
  const rawDueDate = reminder.due_date ?? reminder.dueDate;

  const dateObj = useMemo(() => {
    if (!rawDueDate) return null;
    const parsed = typeof rawDueDate === 'string' ? new Date(rawDueDate) : rawDueDate;
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [rawDueDate]);

  const hasDate = dateObj !== null;

  const isOverdue = useMemo(() => {
    if (isCompleted || !dateObj) return false;
    return dateObj.getTime() < Date.now();
  }, [isCompleted, dateObj]);

  const defaultAccentColor =
    theme.palette.rappel?.main ??
    theme.palette.resourcesColors?.rappel?.main ??
    resourcesColors.rappel.main;

  const accentColor = isOverdue
    ? theme.palette.error?.main ?? theme.palette.incident?.main ?? '#c62828'
    : isCompleted
      ? theme.palette.success?.main ?? '#2e7d32'
      : defaultAccentColor;

  const dayName = useMemo(() => {
    if (!dateObj) return null;
    try {
      const rawDay = format(dateObj, 'EEEE', { locale: fr });
      return rawDay.charAt(0).toUpperCase() + rawDay.slice(1);
    } catch {
      return null;
    }
  }, [dateObj]);

  const dayNumber = useMemo(() => {
    if (!dateObj) return null;
    try {
      return format(dateObj, 'd', { locale: fr });
    } catch {
      return null;
    }
  }, [dateObj]);

  const monthAbbr = useMemo(() => {
    if (!dateObj) return null;
    try {
      return format(dateObj, 'MMM', { locale: fr }).toUpperCase().replace('.', '');
    } catch {
      return null;
    }
  }, [dateObj]);

  const fullDate = useMemo(() => {
    if (!dateObj) return null;
    try {
      return format(dateObj, 'd MMMM yyyy', { locale: fr });
    } catch {
      return null;
    }
  }, [dateObj]);

  const timeText = useMemo(() => {
    if (!dateObj) return null;
    try {
      const hours = dateObj.getHours();
      const minutes = dateObj.getMinutes();
      if (hours !== 0 || minutes !== 0) {
        return format(dateObj, 'HH:mm');
      }
      return null;
    } catch {
      return null;
    }
  }, [dateObj]);

  const recurrenceType = reminder.recurrence_type ?? reminder.recurrenceType;
  const recurrenceValue = reminder.recurrence_value ?? reminder.recurrenceValue;
  const recurrenceLabel = useMemo(() => {
    return formatReminderRecurrence(recurrenceType, recurrenceValue);
  }, [recurrenceType, recurrenceValue]);

  const providerName =
    reminder.providerName?.trim() ||
    reminder.provider_name?.trim() ||
    null;

  const showProvider = Boolean(
    (props.displayProvider ?? true) &&
    (props.renderProvider || providerName || reminder.provider_id || reminder.providerId),
  );

  const subjectName =
    reminder.subjectName?.trim() ||
    reminder.subject_name?.trim() ||
    null;

  const showSubject = Boolean(
    (props.displaySubject ?? true) &&
    (props.renderSubject || subjectName || reminder.subject_id || reminder.subjectId),
  );

  const hasMetadata = showProvider || showSubject;

  const description = reminder.description?.trim() || null;
  const hasDescription = Boolean(description && description.length > 0);

  function handleComplete() {
    props.onComplete?.(reminder);
  }

  function handleCreateEvent() {
    props.onCreateEvent?.(reminder);
  }

  function handleReopen() {
    props.onReopen?.(reminder);
  }

  function handleEdit() {
    props.onEdit?.(reminder);
  }

  function handleDelete() {
    props.onDelete?.(reminder);
  }

  const actions = useMemo(() => {
    const customActions = (props.actions ?? []).filter((action): action is ReminderCardAction => Boolean(action));
    const hasCustomEdit = customActions.some((a) => 'id' in a && a.id === 'edit');
    const hasCustomDelete = customActions.some((a) => 'id' in a && a.id === 'delete');

    const result: ReminderCardAction[] = [];

    if (props.onEdit && !hasCustomEdit) {
      result.push({
        id: 'edit',
        label: 'Modifier',
        icon: React.createElement(EditIcon, { fontSize: 'small' }),
        onClick: handleEdit,
      });
    }

    result.push(...customActions);

    if (props.onDelete && !hasCustomDelete) {
      result.push({
        id: 'delete',
        label: 'Supprimer',
        icon: React.createElement(DeleteIcon, { fontSize: 'small' }),
        onClick: handleDelete,
      });
    }

    return result;
  }, [props.actions, props.onEdit, props.onDelete, reminder]);

  const hasActions = actions.length > 0;

  const condensedActions = computeCondensedActions({
    isCompleted,
    isCompleting: props.isCompleting,
    onComplete: props.onComplete ? handleComplete : undefined,
    onCreateEvent: props.onCreateEvent ? handleCreateEvent : undefined,
    onReopen: props.onReopen ? handleReopen : undefined,
    managementActions: actions,
  });
  const hasFooterActions = Boolean(
    props.footerActions ||
    (!isCompleted && (props.onComplete || props.onCreateEvent)) ||
    (isCompleted && props.onReopen),
  );

  const isClickable = Boolean(props.href || props.onClick);

  const actionsButtonTooltip = props.actionsButtonTooltip ?? 'Options du rappel';
  const actionsButtonAriaLabel = props.actionsButtonAriaLabel ?? actionsButtonTooltip;

  return {
    reminder,
    isClickable,
    title: reminder.title,
    accentColor,
    hasDate,
    dayName,
    dayNumber,
    monthAbbr,
    fullDate,
    timeText,
    isCompleted,
    isOverdue,
    recurrenceLabel,
    providerName,
    showProvider,
    subjectName,
    showSubject,
    hasMetadata,
    description,
    hasDescription,
    actions,
    hasActions,
    condensedActions,
    hasFooterActions,
    actionsButtonTooltip,
    actionsButtonAriaLabel,
    handleComplete,
    handleCreateEvent,
    handleReopen,
  };
}
